import React, { useEffect, useState } from 'react'
import { StyleSheet, ViewProps } from 'react-native'
import { ObjectSchema, object, string } from 'yup'
import { useTranslation } from 'react-i18next'
import { Color, Hyperlink, Spacing } from 'zens-storybook'
import { Country, Place, useCountry, useStore, useTimeZone } from 'zens-store'
import { Form, NavigationConfirm, GeoInput, FormProps, FormGroupConfig, FormFieldConfig } from '@dms/component'

import { observer } from 'mobx-react'

import { CountryInput } from '../country'

const styles = StyleSheet.create({
  manualInput: {
    marginLeft: Spacing.M,
  },
  divider: {
    backgroundColor: Color.transparent,
    marginTop: 0,
    marginBottom: 0,
    height: 0,
  },
})

interface Props extends Omit<FormProps, 'config' | 'schema'> {
  config?: FormFieldConfig | FormGroupConfig
  schema?: ObjectSchema<any>
}
const AddressForm = (props: Props) => {
  const { draft, schema, buttons, config, ...otherProps } = props
  const [inputManual, setInputManual] = useState(false)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const { geo } = useStore()
  const { t } = useTranslation()
  const timeZone = useTimeZone({ lat: draft.data.address?.latitude, lng: draft.data.address?.longitude })
  const country = useCountry({ lat: draft.data.address?.latitude, lng: draft.data.address?.longitude })

  const hasAddress =
    draft.data.address?.address_line_1 ||
    draft.data.address?.city ||
    draft.data.address?.zipcode ||
    draft.data.address?.country_code

  useEffect(() => {
    setInputManual(!!hasAddress)
  }, [])

  const updateGeo = (place: Place) => {
    draft.update('address', {
      address_line_1: place.address,
      address_line_2: '',
      city: place.city,
      zipcode: place.zipCode,
      country_code: place.countryCode,
      latitude: place.latitude,
      longitude: place.longitude,
    })
    setInputManual(true)
  }

  useEffect(() => {
    draft.update('address.time_zone', timeZone.data?.id)
  }, [timeZone.data])

  function updateFieldErrors(errors: Record<string, string>) {
    const { address, ...otherErrors } = errors
    setFieldErrors({ ...otherErrors, 'address.address_line_1': otherErrors['address.address_line_1'] || address })
  }

  const addressConfig = {
    fields: inputManual
      ? [
          {
            key: 'address.address_line_1',
            errorKeys: ['address'],
            label: t('Address line 1'),
            right: (
              <Hyperlink
                title={t('Find location via Maps')}
                style={styles.manualInput}
                onPress={() => setInputManual(false)}
                testID="AddressForm.LinkMaps"
              />
            ),
            fieldProps: {
              testID: 'AddressForm.AddressLine1',
            },
          },
          {
            key: 'address.address_line_2',
            label: t('Address line 2'),
            optional: true,
            fieldProps: {
              testID: 'AddressForm.AddressLine2',
            },
          },
          {
            key: 'address.city',
            label: t('City'),
            fieldProps: {
              testID: 'AddressForm.City',
            },
          },
          {
            key: 'address.zipcode',
            label: t('Zip/Postal code'),
            fieldProps: {
              testID: 'AddressForm.ZipCode',
            },
          },
          {
            key: 'address.country_code',
            label: t('Country'),
            FieldInputComponent: CountryInput,
            fieldProps: {
              testID: 'AddressForm.Country',
              value: country.data,
              onChange: (country: Country) => draft.update('address.country_code', country?.code),
            },
          },
          {
            key: 'address.time_zone',
            label: t('Timezone'),
            fieldProps: {
              value: timeZone.data?.name,
              testID: 'AddressForm.Timezone',
              disabled: true,
            },
          },
        ]
      : [
          {
            key: 'address.search',
            label: t('Find location'),
            right: (
              <Hyperlink
                title={t('Add address manually')}
                style={styles.manualInput}
                onPress={() => setInputManual(true)}
                testID="AddressForm.LinkManual"
              />
            ),
            FieldInputComponent: GeoInput,
            fieldProps: {
              onChange: updateGeo,
            },
          },
        ],
    dividerStyle: styles.divider,
  }

  const allConfig: FormGroupConfig = (config as FormGroupConfig)?.groups
    ? {
        groups: [...(config as FormGroupConfig).groups, addressConfig],
      }
    : {
        groups: [...(config ? [config as FormFieldConfig] : []), addressConfig],
      }
  const addressSchema = object({
    address: object({
      address_line_1: string().required(t('Address is required')),
      city: string().required(t('City is required')),
      zipcode: string().required(t('Zip/Postal code is required')),
      country_code: string().required(t('Country is required')),
      ...(!inputManual ? { search: string().required(t('Address is required')) } : {}),
    }).test('geocode?', t('Address not found'), async (address) => {
      // If empty address let above validators fail
      if (!address.address_line_1 || !address.city || !address.zipcode || !address.country_code) return true

      const coords = await geo.getCoordinates({
        address: `${address.address_line_1} ${address.city} ${address.zipcode}`,
        country: address.country_code,
      })
      draft.update('address.latitude', coords?.lat)
      draft.update('address.longitude', coords?.lng)

      const timeZone = await geo.getTimeZone(coords)
      draft.update('address.time_zone', timeZone?.id)

      return !!coords?.lat && !!coords?.lng && !!timeZone?.id
    }),
  })

  const allSchema = schema ? addressSchema.concat(schema) : addressSchema

  return (
    <>
      <Form
        draft={draft}
        config={allConfig}
        schema={allSchema}
        buttons={buttons}
        errors={fieldErrors}
        onError={updateFieldErrors}
        {...otherProps}
      />
      <NavigationConfirm draft={draft} />
    </>
  )
}

interface SkeletonProps extends ViewProps {
  config?: FormFieldConfig
  numberOfbuttons?: number | number[]
  inputManual?: boolean
}
const Skeleton = (props: SkeletonProps) => {
  const { config, inputManual, style, ...otherProps } = props
  const { t } = useTranslation()
  const allConfig = {
    groups: [
      ...(config ? [config] : []),
      {
        fields: inputManual
          ? [
              {
                key: 'address.address_line_1',
                label: t('Address line 1'),
              },
              {
                key: 'address.address_line_2',
                label: t('Address line 2'),
              },
              {
                key: 'address.city',
                label: t('City'),
              },
              {
                key: 'address.zipcode',
                label: t('Zip/Postal code'),
              },
              {
                key: 'address.country_code',
                label: t('Country'),
              },
              {
                key: 'address.time_zone',
                label: t('Timezone'),
              },
            ]
          : [
              {
                key: 'search',
                label: t('Find location'),
              },
            ],
        dividerStyle: styles.divider,
      },
    ],
  }
  return <Form.Skeleton style={style} config={allConfig} {...otherProps} />
}
AddressForm.Skeleton = Skeleton

export default observer(AddressForm)
